import { SkeletonBar } from '@/components/feedback/ResultStates';
import styles from './JobSkeletons.module.css';

/**
 * Job result skeletons, shaped like the cards they stand in for. The same silhouettes
 * shimmer while loading and are reused, faded, as the backdrop of the empty state.
 */

// Job ID, location, experience share a line; skills take the next
const FACT_WIDTHS = [96, 148, 56];

/** JobCardFrame: title + actions, the inline facts, the skills line, then the footer. */
export function JobCardSkeleton() {
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={styles.cardHead}>
        <SkeletonBar width="58%" height={16} />
        <SkeletonBar width={28} height={28} />
      </div>
      <div className={styles.facts}>
        {FACT_WIDTHS.map((width) => (
          <div key={width} className={styles.fact}>
            <SkeletonBar width={14} height={14} />
            <SkeletonBar width={width} />
          </div>
        ))}
        <div className={`${styles.fact} ${styles.factWide}`}>
          <SkeletonBar width={14} height={14} />
          <SkeletonBar width="72%" />
        </div>
      </div>
      <div className={styles.footer}>
        <SkeletonBar width="32%" />
      </div>
    </div>
  );
}

export function JobCardSkeletons({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <JobCardSkeleton key={i} />
      ))}
    </>
  );
}

/** HomeJobDetailPanel: title, meta and apply buttons, then the skills and description blocks. */
export function JobDetailSkeleton() {
  return (
    <div className={styles.panel} aria-hidden="true">
      <div className={styles.panelHead}>
        <SkeletonBar width="62%" height={20} />
        <SkeletonBar width="38%" />
        <div className={styles.panelActions}>
          <SkeletonBar width={128} height={38} />
          <SkeletonBar width={104} height={38} />
        </div>
      </div>
      <div className={styles.panelBody}>
        <div className={styles.panelBlock}>
          <SkeletonBar width={84} height={10} />
          <div className={styles.tags}>
            {[76, 92, 64, 108].map((w) => (
              <SkeletonBar key={w} width={w} height={26} />
            ))}
          </div>
        </div>
        <div className={styles.panelBlock}>
          <SkeletonBar width={96} height={10} />
          <SkeletonBar />
          <SkeletonBar width="94%" />
          <SkeletonBar width="82%" />
          <SkeletonBar width="60%" />
        </div>
      </div>
    </div>
  );
}

/** The list/detail layout with nothing in it — the empty state's backdrop. */
export function JobResultsGhost() {
  return (
    <div className={styles.ghostSplit}>
      <div className={styles.ghostList}>
        <JobCardSkeleton />
        <JobCardSkeleton />
      </div>
      <div className={styles.ghostPanel}>
        <JobDetailSkeleton />
      </div>
    </div>
  );
}
